import React from "react";
import {
  Paper,
  Box,
  Typography,
  Stack,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Pagination,
  useTheme,
  Alert,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  FileDownload as FileDownloadIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import Greeting from "../Layout/Greeting";
import { Navigate } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

const FullHmoPage = ({ hmoInfo, hmoDetails }) => {
  const theme = useTheme();

  const getStatusChipColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Limited":
        return "warning";
      case "Inactive":
      case "Expired":
        return "error";
      default:
        return "default";
    }
  };

  const handleStatusClick = (row) => {
    console.log("Status clicked for:", row);
  };

  if (!hmoInfo && !hmoDetails) return null;

  // Use hmoDetails if available, otherwise fallback to hmoInfo
  const providerName =
    hmoDetails?.provider?.vendorName ||
    hmoDetails?.provider?.name ||
    hmoInfo?.name ||
    "HMO Provider";
  // Display package name if available
  const packageName = hmoDetails?.package?.name || "";
  const membershipId =
    hmoDetails?.patientHMOId || hmoInfo?.patientHMOId || "N/A";
  const membershipValidity = hmoDetails?.membershipValidityDate
    ? new Date(hmoDetails.membershipValidityDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : hmoDetails?.membershipValidity
      ? new Date(hmoDetails.membershipValidity).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : hmoInfo?.endDate || "N/A";
  const isActive =
    hmoDetails?.isActive !== undefined ? hmoDetails.isActive : true;
  const statusLabel = isActive ? "Active" : "Expired";
  const premium = hmoDetails?.package?.premium || hmoInfo?.premium || "0";
  const notes = hmoDetails?.notes || hmoInfo?.notes || "";
  const isReferred = hmoDetails?.isReferred || hmoInfo?.isReferred || false;

  // Provider contact information
  const phoneNumber = hmoDetails?.provider?.phoneNumber || "";
  const altPhoneNumber = hmoDetails?.provider?.altPhoneNumber || "";
  const contactPerson = hmoDetails?.provider?.contactPerson || "";

  // Get package benefits from hmoDetails or fallback to hmoInfo
  const packageBenefits = hmoDetails?.packages?.packageBenefits || [];

  const {
    benefits = [],
    outpatientLimits = [],
    inpatientLimits = { total: 0, items: [] },
    additionalServices = [],
    premiumLabel = "Annual Premium",
    iconMap = {},
  } = hmoInfo || {};

  return (
    <div className="hmo-content w-100">
      {/* HMO Status Alert */}
      {hmoDetails && (
        <Box sx={{ mb: 3 }}>
          <Alert
            severity={isActive ? "success" : "error"}
            sx={{
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                HMO Status:
              </Typography>
              <Chip
                label={statusLabel}
                color={getStatusChipColor(statusLabel)}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Alert>
        </Box>
      )}

      <div style={{ marginTop: "8px" }} className="hmo-package-header">
        <h1
          style={{
            fontSize: "clamp(1.1rem, 4vw, 1.5rem)",
            wordBreak: "break-word",
          }}
        >
          <span className="axa-text">{providerName}</span>
          {packageName && <span className="gold-text"> / {packageName}</span>}
        </h1>
        <div
          className="package-info"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            fontSize: "clamp(0.75rem, 2.5vw, 0.9rem)",
          }}
        >
          <span className="end-date">End Date: {membershipValidity}</span>
          {membershipId !== "N/A" && (
            <>
              <span className="divider">|</span>
              <span className="membership-id">ID: {membershipId}</span>
            </>
          )}
        </div>

        {/* Provider Contact Information */}
        {(phoneNumber || altPhoneNumber || contactPerson) && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "#f0f8f5",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, color: "#4caf50" }}
            >
              Provider Contact Information
            </Typography>
            <Stack spacing={0.5}>
              {phoneNumber && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Phone:</strong> {phoneNumber}
                </Typography>
              )}
              {altPhoneNumber && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Alt Phone:</strong> {altPhoneNumber}
                </Typography>
              )}
              {contactPerson && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Contact Person:</strong> {contactPerson}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {notes && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "#fff8e1",
              borderRadius: 1,
              border: "1px solid #ffe082",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Notes:</strong> {notes}
            </Typography>
          </Box>
        )}
        {isReferred && (
          <Chip
            label="Referred Patient"
            color="info"
            size="small"
            sx={{ mt: 1 }}
          />
        )}
      </div>

      <div className="benefits-section">
        <div className="section-header hospital-accessible">
          <span>Category Hospital Accessible</span>
        </div>
        <div className="benefits-subtitle">
          <span>Out Patient Care, General and Specialist Consultation</span>
          {/* <span className="grade">A+B+C</span> */}
        </div>

        <Paper
          sx={{
            p: 3,
            margin: 0,
            boxShadow: "none",
            backgroundColor: "transparent",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              All Benefits
            </Typography>
            <Stack direction="row" spacing={2}>
              {/* <Button variant="outlined" startIcon={<FilterListIcon />} sx={{ color: '#4caf50', borderColor: '#4caf50', '&:hover': { backgroundColor: '#4caf5008' } }}>Filter</Button> */}
              {/* <Button variant="outlined" startIcon={<FileDownloadIcon />} sx={{ color: '#4caf50', borderColor: '#4caf50', '&:hover': { backgroundColor: '#4caf5008' } }}>Export</Button> */}
              {/* <Button variant="outlined" startIcon={<SortIcon />} sx={{ color: '#4caf50', borderColor: '#4caf50', '&:hover': { backgroundColor: '#4caf5008' } }}>Sort By</Button> */}
            </Stack>
          </Box>

          <TableContainer
            sx={{
              borderRadius: "8px",
              overflow: "hidden",
              mb: 3,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table sx={{ minWidth: { xs: "600px", sm: "auto" } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    S/N
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    Category
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    Benefit Provision
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    Benefit Limit (₦)
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Show package benefits from API if available */}
                {packageBenefits?.length > 0 ? (
                  packageBenefits.map((benefit, index) => (
                    <TableRow
                      key={benefit.id || index}
                      sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                    >
                      <TableCell
                        sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                        >
                          {benefit.benefitName || benefit.name || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          color="text.secondary"
                          sx={{
                            maxWidth: "400px",
                            fontSize: { xs: "0.7rem", sm: "0.875rem" },
                          }}
                        >
                          {benefit.description ||
                            benefit.benefitDescription ||
                            "No description available"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "#4caf50",
                            fontSize: { xs: "0.7rem", sm: "0.875rem" },
                          }}
                        >
                          {benefit.limit
                            ? `₦${benefit.limit.toLocaleString()}`
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label="Active"
                          color="success"
                          size="small"
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : benefits?.length > 0 ? (
                  // Fallback to old benefits structure
                  benefits.map((row, index) => {
                    const IconComponent = iconMap[row.icon] || (() => null);
                    return (
                      <TableRow
                        key={row.id}
                        sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                backgroundColor: "#4caf5015",
                              }}
                            >
                              <IconComponent
                                sx={{ color: "#4caf50", fontSize: "1rem" }}
                              />
                            </Box>
                            <Typography>{row.category}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography
                            color="text.secondary"
                            sx={{ maxWidth: "400px" }}
                          >
                            {row.benefit}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{ fontWeight: 600, color: "#4caf50" }}
                          >
                            {row.amount}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            color={getStatusChipColor(row.status)}
                            size="small"
                            onClick={() => handleStatusClick(row)}
                            sx={{
                              cursor: "pointer",
                              "&:hover": {
                                transform: "scale(1.05)",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              },
                              transition: "all 0.2s ease-in-out",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No benefits information available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
              pt: 2,
              borderTop: "1px solid #e9ecef",
            }}
          >
            <Pagination count={1} page={1} color="primary" />
          </Box>
        </Paper>
      </div>

      {/* Out-patient and In-patient limits commented out per request */}
      {/* 
      <div className="limit-section out-patient">
        <div className="limit-header">
          <span>Out-patient limit</span>
        </div>
        <div className="limit-content">
          {outpatientLimits.map((item, idx) => (
            <div key={idx} className="limit-item">
              <span>{item.title}</span>
              <span className={`status ${item.status.toLowerCase()}`}>{item.status}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="limit-section in-patient">
        <div className="limit-header">
          <span>In-patient limit</span>
          <span className="amount">₦{inpatientLimits.total}</span>
        </div>
        <div className="limit-content">
          {inpatientLimits.items.map((item, idx) => (
            <div key={idx} className="limit-item">
              <span>{item.title}</span>
              <span className={`status ${item.status.toLowerCase()}`}>{item.status}</span>
            </div>
          ))}
        </div>
      </div>
      */}

      {additionalServices.map((section, i) => (
        <div key={i} className="service-section">
          <div className="service-header">
            {section.icon && <section.icon className="service-icon" />}
            <span>{section.title}</span>
          </div>
          {section.items.map((item, j) => (
            <div key={j} className="service-item">
              <span>{item.description}</span>
              <span className="limit-amount">{item.amount}</span>
            </div>
          ))}
        </div>
      ))}

      {/* Premium section hidden per request */}
    </div>
  );
};

export default FullHmoPage;
